import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Inbox, Loader2, Download, Search, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";

/**
 * LeadsRepository — repositório único de TODAS as capturas do ecossistema:
 *   · Formulários das Landing Pages (simples e em etapas) + pop-up de saída
 *   · Bot Marina (site e LPs), com a conversa completa
 *   · Formulário de contato institucional
 *   · Downloads com lead-gate (PDF dos Insights)
 *   · Páginas Geo-SEO
 *
 * Backend: GET /admin/all-leads (normalizado) e /admin/all-leads.csv (export).
 */
const DAYS = [
  { v: 0, label: "Tudo" },
  { v: 7, label: "7 dias" },
  { v: 30, label: "30 dias" },
  { v: 90, label: "90 dias" },
];

const GROUP_LABEL = {
  lp: "Landing Pages",
  chatbot: "Bot Marina",
  contato: "Contato",
  "pdf-gate": "Materiais (PDF)",
  geo: "Geo-SEO",
  concierge: "Mordomo",
  outros: "Outros",
};

export default function LeadsRepository() {
  const [days, setDays] = useState(0);
  const [group, setGroup] = useState("");
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setTerm(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/all-leads?days=${days}&source=${encodeURIComponent(group)}&q=${encodeURIComponent(term)}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days, group, term]);

  const rows = data?.rows || [];
  const counts = data?.counts || {};
  const groupKeys = useMemo(() => Object.keys(GROUP_LABEL), []);
  const csvUrl = `${API_BASE}/admin/all-leads.csv?days=${days}&source=${encodeURIComponent(group)}&q=${encodeURIComponent(term)}`;

  return (
    <div data-testid="leads-repository-page">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <Inbox className="w-6 h-6 text-accent-orange" />
          <h1 className="font-display text-4xl">Repositório de Leads</h1>
        </div>
        <a href={csvUrl} data-testid="leads-export-csv"
          className="text-xs bg-black text-white font-semibold px-4 py-2.5 rounded-full flex items-center gap-1.5 hover:bg-accent-orange">
          <Download className="w-3.5 h-3.5" /> Exportar CSV
        </a>
      </div>
      <p className="text-sm text-neutral-500 mb-6 max-w-3xl">
        Tudo que foi capturado no ecossistema, num só lugar: formulários das LPs (simples e em etapas), pop-up de saída,
        bot Marina (site e LPs, com a conversa completa), formulário de contato, downloads de material e páginas Geo-SEO.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {DAYS.map((d) => (
          <button key={d.v} onClick={() => setDays(d.v)} data-testid={`leads-days-${d.v}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full ${days === d.v ? "bg-black text-white" : "border border-black/15"}`}>
            {d.label}
          </button>
        ))}
        <div className="w-px h-6 bg-black/10 mx-1" />
        <button onClick={() => setGroup("")} data-testid="leads-group-all"
          className={`text-xs font-semibold px-3 py-1.5 rounded-full ${group === "" ? "bg-accent-orange text-white" : "border border-black/15"}`}>
          Todas as origens
        </button>
        {groupKeys.map((g) => (
          <button key={g} onClick={() => setGroup(g)} data-testid={`leads-group-${g}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full ${group === g ? "bg-accent-orange text-white" : "border border-black/15"}`}>
            {GROUP_LABEL[g]}{counts[g] ? ` (${counts[g]})` : ""}
          </button>
        ))}
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="leads-search"
          placeholder="Buscar por nome, email, telefone, mensagem…"
          className="w-full border border-black/15 pl-9 pr-3 py-2.5 rounded-full text-sm" />
      </div>

      <div className="border border-black/10 rounded-md overflow-hidden">
        <div className="bg-neutral-50 border-b border-black/10 px-4 py-3 flex items-center justify-between">
          <div className="text-xs uppercase font-semibold tracking-widest text-neutral-700">
            {loading ? "Carregando…" : `${rows.length} lead${rows.length === 1 ? "" : "s"}`}
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-neutral-500 py-16 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-500 py-16 text-center">Nenhum lead encontrado com esses filtros.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-neutral-500 border-b border-black/10">
              <tr>
                <th className="w-8" />
                <th className="text-left px-3 py-2">Quando</th>
                <th className="text-left px-3 py-2">Origem</th>
                <th className="text-left px-3 py-2">Nome</th>
                <th className="text-left px-3 py-2">Contato</th>
                <th className="text-left px-3 py-2">Mensagem</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = !!open[r.id];
                return (
                  <React.Fragment key={r.id}>
                    <tr className="border-b border-black/5 hover:bg-neutral-50 cursor-pointer align-top"
                      onClick={() => setOpen((s) => ({ ...s, [r.id]: !s[r.id] }))}
                      data-testid={`lead-row-${r.id}`}>
                      <td className="px-2 py-2.5 text-neutral-400">
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-neutral-500 whitespace-nowrap">
                        {(r.created_at || "").slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-[10px] uppercase font-semibold tracking-widest bg-black/[0.06] px-2 py-0.5 rounded-full">
                          {GROUP_LABEL[r.group] || r.group}
                        </span>
                        <div className="text-[10px] text-neutral-400 font-mono mt-1">{r.source}</div>
                      </td>
                      <td className="px-3 py-2.5 font-semibold">{r.name || <span className="text-neutral-400">—</span>}</td>
                      <td className="px-3 py-2.5 text-[12px]">
                        {r.email && <div>{r.email}</div>}
                        {r.phone && <div className="font-mono">{r.phone}</div>}
                        {!r.email && !r.phone && <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-[12px] text-neutral-600 max-w-md truncate">{r.message || "—"}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-neutral-50 border-b border-black/10">
                        <td />
                        <td colSpan={5} className="px-3 py-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-[10px] uppercase font-semibold tracking-widest text-neutral-500 mb-1">Dados capturados</div>
                              <pre className="text-[11px] bg-white border border-black/10 rounded p-2 whitespace-pre-wrap max-h-64 overflow-auto">
                                {JSON.stringify(r.extras || {}, null, 2)}
                              </pre>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <div className="text-[10px] uppercase font-semibold tracking-widest text-neutral-500 mb-1">UTM / Origem</div>
                                <pre className="text-[11px] bg-white border border-black/10 rounded p-2 whitespace-pre-wrap">
                                  {JSON.stringify(r.utm || {}, null, 2)}
                                </pre>
                              </div>
                              {r.page_url && (
                                <a href={r.page_url} target="_blank" rel="noopener noreferrer"
                                  className="text-xs text-neutral-600 hover:text-black inline-flex items-center gap-1">
                                  <ExternalLink className="w-3 h-3" /> {r.page_url}
                                </a>
                              )}
                              {r.phone && (
                                <a href={`https://wa.me/${String(r.phone).replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
                                  className="block text-xs bg-[#25D366] text-white font-semibold px-4 py-2 rounded-full w-fit">
                                  Abrir WhatsApp
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
